import React, { useCallback, useEffect, useState } from "react";
import api from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, Truck, User, RefreshCw } from "lucide-react";

// Catalog Fulfillment PR 4 (Aug 2026) — tenant operational lifecycle UI
// for an Own Delivery order (the PR3-implemented phase axis in
// `retail/delivery/dispatch/domain/operational_phases.py`). Rendered
// inside OrderDetailsDrawer ONLY when `orderDoc?.fulfillment_choice ===
// "own"` and `orderDoc?.local_delivery_id` is present — a completely
// separate gate and component from the pre-existing, Uber-only
// `OrderLocalDeliveryPanel` (`fulfillment.mode === "delivery"`), so the
// two can never both render for the same order.
//
// Every transition goes through `POST /retail/orders/{id}/own-delivery/
// transition`, which is the SOLE source of truth for which transitions
// are legal (`can_transition_own_delivery_phase`) — this component only
// mirrors that table to grey out obviously-invalid buttons; it never
// invents a transition the server wouldn't also accept, and a stale
// client-side guess is always caught server-side (idempotent replay or
// a `conflict`/`invalid_transition` error, never a silent success).
//
// Driver name/phone/notes/ETA are tenant-operational PII: they live
// ONLY here (surfaced via the dedicated GET .../own-delivery endpoint,
// never embedded in any customer-facing or public response) and are
// never sent to any audit/reconciliation/customer-notification surface
// from this component.

const PHASE_LABEL = {
  accepted: "Accepted",
  preparing: "Preparing",
  ready_for_delivery: "Ready for Delivery",
  driver_assigned: "Driver Assigned",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  delivery_failed: "Delivery Failed",
  delivery_cancelled: "Delivery Cancelled",
};

const PHASE_COLOR = {
  accepted: "text-slate-700 bg-slate-100 border-slate-200",
  preparing: "text-sky-700 bg-sky-50 border-sky-200",
  ready_for_delivery: "text-sky-700 bg-sky-50 border-sky-200",
  driver_assigned: "text-indigo-700 bg-indigo-50 border-indigo-200",
  out_for_delivery: "text-indigo-700 bg-indigo-50 border-indigo-200",
  delivered: "text-emerald-700 bg-emerald-50 border-emerald-200",
  delivery_failed: "text-rose-700 bg-rose-50 border-rose-200",
  delivery_cancelled: "text-rose-700 bg-rose-50 border-rose-200",
};

// End-to-End Correction — Own Delivery -> Kitchen -> Delivery Handoff
// (Aug 2026). Kitchen preparation is now exclusively KDS-operator-
// driven once released: "preparing" no longer appears as a next phase
// from "accepted" (that hop happens server-side, in lockstep with the
// KDS operator's own "Start Preparing" click — see `routes/retail.py::
// update_order_state`'s own-delivery cascade), and "ready_for_delivery"
// no longer appears as a next phase from "preparing" (that hop happens
// automatically when the KDS operator marks the order Complete). Every
// other edge is unchanged. Mirrors `ALLOWED_OWN_DELIVERY_PHASE_
// TRANSITIONS` minus those two tenant-initiated edges — a client-side
// convenience only, never independently authoritative.
const NEXT_PHASES = {
  accepted: ["delivery_cancelled"],
  preparing: ["delivery_cancelled"],
  ready_for_delivery: ["driver_assigned", "delivery_cancelled"],
  driver_assigned: ["out_for_delivery", "delivery_cancelled"],
  out_for_delivery: ["delivered", "delivery_failed", "delivery_cancelled"],
  delivered: [],
  delivery_failed: [],
  delivery_cancelled: [],
};

const TERMINAL_PHASES = new Set(["delivered", "delivery_failed", "delivery_cancelled"]);

// Read-only reflection of the order/KDS lifecycle (`retail/orders_state.
// py`) — this panel never offers a control that drives these values;
// it only displays whatever the KDS operator has already committed.
const KITCHEN_STATE_LABEL = {
  created: "New",
  confirmed: "New",
  preparing: "Preparing",
  ready: "Ready",
  completed: "Complete",
  cancelled: "Cancelled",
};

export default function OwnDeliveryOperationalPanel({ orderId }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [submitting, setSubmitting] = useState(false);
  const [pendingTarget, setPendingTarget] = useState(null);
  const [driverName, setDriverName] = useState("");
  const [driverPhone, setDriverPhone] = useState("");
  const [driverNotes, setDriverNotes] = useState("");
  const [driverEta, setDriverEta] = useState("");
  const [failureReason, setFailureReason] = useState("");
  const [cancellationReason, setCancellationReason] = useState("");

  const load = useCallback(() => {
    setState((s) => ({ loading: !s.data, error: null, data: s.data }));
    api.get(`/retail/orders/${orderId}/own-delivery`)
      .then((r) => setState({ loading: false, error: null, data: r.data }))
      .catch((e) => {
        if (e?.response?.status === 404) {
          setState({ loading: false, error: "not_own_delivery", data: null });
        } else {
          setState({ loading: false, error: "load_failed", data: null });
        }
      });
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  // End-to-End Correction — Own Delivery -> Kitchen -> Delivery Handoff
  // (Aug 2026). The existing "Start Preparing" label/control is REUSED
  // here for its new required meaning: release this accepted, paid Own
  // Delivery order to the Kitchen Display as a NEW ticket. It does NOT
  // advance `own_delivery_phase` (stays "accepted") and does NOT touch
  // the order/KDS state — the server's sole effect is committing
  // `kitchen_release_committed_at`, which is what makes the order
  // visible to KDS's own `active_only=true` query. From this point on,
  // only the KDS operator drives Preparing/Ready/Complete.
  const [releasing, setReleasing] = useState(false);
  const releaseToKitchen = async () => {
    if (releasing) return;
    setReleasing(true);
    try {
      const { data } = await api.post(`/retail/orders/${orderId}/own-delivery/release-to-kitchen`);
      if (data.status === "already_released") {
        toast.message("This order was already sent to the kitchen.");
      } else {
        toast.success("Sent to the kitchen as a new ticket.");
      }
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error((det && typeof det === "object" && det.message) || "Could not send this order to the kitchen.");
    } finally {
      setReleasing(false);
    }
  };

  const submitTransition = async (toPhase, extra = {}) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/retail/orders/${orderId}/own-delivery/transition`, {
        to_phase: toPhase, ...extra,
      });
      if (data.status === "already_at_phase") {
        toast.message("This order's delivery status was already updated.");
      } else {
        toast.success(`Marked as ${PHASE_LABEL[toPhase] || toPhase}.`);
      }
      setPendingTarget(null);
      setDriverName(""); setDriverPhone(""); setDriverNotes(""); setDriverEta("");
      setFailureReason(""); setCancellationReason("");
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error((det && typeof det === "object" && det.message) || "Could not update this delivery's status.");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="py-4 grid place-items-center text-slate-400" data-testid="own-delivery-panel-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }
  if (state.error === "not_own_delivery") return null;
  if (state.error) {
    return (
      <div className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="own-delivery-panel-error">
        Could not load Own Delivery details.
      </div>
    );
  }
  if (!state.data) return null;

  const d = state.data;
  const phase = d.own_delivery_phase || "accepted";
  const nextPhases = NEXT_PHASES[phase] || [];
  const isTerminal = TERMINAL_PHASES.has(phase);
  // End-to-End Correction — Own Delivery -> Kitchen -> Delivery Handoff
  // (Aug 2026). Kitchen work (New/Preparing/Ready) is read-only here —
  // rendered only while it's still relevant, i.e. before the phase has
  // advanced past what the kitchen owns.
  const kitchenReleased = Boolean(d.kitchen_release_committed_at);
  const kitchenState = d.order_kitchen_state || "created";
  const showKitchenStatus = kitchenReleased && (phase === "accepted" || phase === "preparing");

  return (
    <section data-testid="own-delivery-operational-panel" className="space-y-4">
      <div className="flex items-center gap-2">
        <Truck className="w-4 h-4 text-orange-600" />
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Own Delivery</div>
        <span
          className={`ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${PHASE_COLOR[phase] || "text-slate-600 bg-slate-100 border-slate-200"}`}
          data-testid="own-delivery-panel-phase"
        >
          {PHASE_LABEL[phase] || phase}
        </span>
        {showKitchenStatus && (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border text-amber-700 bg-amber-50 border-amber-200"
            data-testid="own-delivery-panel-kitchen-status"
            title="Set by the Kitchen Display — not controlled here"
          >
            Kitchen: {KITCHEN_STATE_LABEL[kitchenState] || kitchenState}
          </span>
        )}
        <button type="button" onClick={load} className="ml-auto text-slate-400 hover:text-slate-700" title="Refresh" data-testid="own-delivery-panel-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {phase === "accepted" && !kitchenReleased && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="own-delivery-panel-release-prompt">
          <div className="text-xs text-amber-900">
            This order is accepted and paid, but has not yet been sent to the kitchen.
          </div>
          <button
            type="button"
            disabled={releasing}
            onClick={releaseToKitchen}
            data-testid="own-delivery-action-release-to-kitchen"
            className="w-full px-3 py-2 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-60"
          >
            {releasing ? "Sending…" : "Start Preparing"}
          </button>
        </div>
      )}

      {(d.driver_name || d.driver_phone || d.driver_eta) && (
        <div className="rounded-md border border-slate-200 p-3 bg-slate-50/60" data-testid="own-delivery-panel-driver">
          <div className="flex items-center gap-2 mb-1">
            <User className="w-3.5 h-3.5 text-slate-500" />
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Driver</div>
          </div>
          <div className="text-sm text-slate-900">{d.driver_name || "—"}{d.driver_phone ? ` · ${d.driver_phone}` : ""}</div>
          {d.driver_eta && <div className="text-xs text-slate-600 mt-0.5">ETA: {d.driver_eta}</div>}
          {d.driver_notes && <div className="text-xs text-slate-600 mt-0.5">{d.driver_notes}</div>}
        </div>
      )}

      {phase === "delivery_failed" && d.own_delivery_failure_reason && (
        <div className="rounded-md bg-rose-50 border border-rose-200 text-rose-800 text-xs px-3 py-2" data-testid="own-delivery-panel-failure-reason">
          {d.own_delivery_failure_reason}
        </div>
      )}
      {phase === "delivery_cancelled" && d.own_delivery_cancellation_reason && (
        <div className="rounded-md bg-rose-50 border border-rose-200 text-rose-800 text-xs px-3 py-2" data-testid="own-delivery-panel-cancellation-reason">
          {d.own_delivery_cancellation_reason}
        </div>
      )}

      {!isTerminal && (
        <div className="space-y-2" data-testid="own-delivery-panel-actions">
          {pendingTarget === "driver_assigned" ? (
            <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3 space-y-2" data-testid="own-delivery-panel-driver-form">
              <div className="text-xs font-medium text-indigo-900">Assign a driver</div>
              <input
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                placeholder="Driver name"
                data-testid="own-delivery-driver-name-input"
                className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5"
              />
              <input
                value={driverPhone}
                onChange={(e) => setDriverPhone(e.target.value)}
                placeholder="Driver phone"
                data-testid="own-delivery-driver-phone-input"
                className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5"
              />
              <input
                value={driverEta}
                onChange={(e) => setDriverEta(e.target.value)}
                placeholder="ETA (optional)"
                data-testid="own-delivery-driver-eta-input"
                className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5"
              />
              <textarea
                value={driverNotes}
                onChange={(e) => setDriverNotes(e.target.value)}
                placeholder="Assignment notes (optional)"
                data-testid="own-delivery-driver-notes-input"
                className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5"
                rows={2}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={submitting || !driverName.trim() || !driverPhone.trim()}
                  onClick={() => submitTransition("driver_assigned", {
                    driver_name: driverName.trim(), driver_phone: driverPhone.trim(),
                    driver_notes: driverNotes.trim() || undefined, driver_eta: driverEta.trim() || undefined,
                  })}
                  data-testid="own-delivery-driver-assign-confirm"
                  className="flex-1 px-3 py-2 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-60"
                >
                  {submitting ? "Assigning…" : "Confirm Driver Assigned"}
                </button>
                <button type="button" disabled={submitting} onClick={() => setPendingTarget(null)} className="px-3 py-2 rounded-md border border-slate-300 text-xs">
                  Cancel
                </button>
              </div>
            </div>
          ) : pendingTarget === "delivery_failed" ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 space-y-2" data-testid="own-delivery-panel-failure-form">
              <div className="text-xs font-medium text-rose-900">Mark delivery as failed</div>
              <textarea
                value={failureReason}
                onChange={(e) => setFailureReason(e.target.value)}
                placeholder="Reason (visible to your own team only)"
                data-testid="own-delivery-failure-reason-input"
                className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5"
                rows={2}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => submitTransition("delivery_failed", { failure_reason: failureReason.trim() || undefined })}
                  data-testid="own-delivery-failure-confirm"
                  className="flex-1 px-3 py-2 rounded-md bg-rose-600 text-white text-xs font-medium hover:bg-rose-700 disabled:opacity-60"
                >
                  {submitting ? "Saving…" : "Confirm Delivery Failed"}
                </button>
                <button type="button" disabled={submitting} onClick={() => setPendingTarget(null)} className="px-3 py-2 rounded-md border border-slate-300 text-xs">
                  Cancel
                </button>
              </div>
            </div>
          ) : pendingTarget === "delivery_cancelled" ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 space-y-2" data-testid="own-delivery-panel-cancel-form">
              <div className="text-xs font-medium text-rose-900">Cancel this delivery</div>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Reason (visible to your own team only)"
                data-testid="own-delivery-cancel-reason-input"
                className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5"
                rows={2}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => submitTransition("delivery_cancelled", { cancellation_reason: cancellationReason.trim() || undefined })}
                  data-testid="own-delivery-cancel-confirm"
                  className="flex-1 px-3 py-2 rounded-md bg-rose-600 text-white text-xs font-medium hover:bg-rose-700 disabled:opacity-60"
                >
                  {submitting ? "Cancelling…" : "Confirm Cancel Delivery"}
                </button>
                <button type="button" disabled={submitting} onClick={() => setPendingTarget(null)} className="px-3 py-2 rounded-md border border-slate-300 text-xs">
                  Keep Delivery
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {nextPhases.map((next) => {
                const isDestructive = next === "delivery_failed" || next === "delivery_cancelled";
                const needsForm = next === "driver_assigned" || next === "delivery_failed" || next === "delivery_cancelled";
                return (
                  <button
                    key={next}
                    type="button"
                    disabled={submitting}
                    onClick={() => (needsForm ? setPendingTarget(next) : submitTransition(next))}
                    data-testid={`own-delivery-action-${next}`}
                    className={`px-3 py-2 rounded-md text-xs font-medium disabled:opacity-60 ${
                      isDestructive
                        ? "border border-rose-300 text-rose-700 hover:bg-rose-50"
                        : "bg-emerald-600 text-white hover:bg-emerald-700"
                    }`}
                  >
                    {next === "driver_assigned" && "Assign Driver"}
                    {next === "out_for_delivery" && "Mark Out for Delivery"}
                    {next === "delivered" && "Mark Delivered"}
                    {next === "delivery_failed" && "Delivery Failed"}
                    {next === "delivery_cancelled" && "Cancel Delivery"}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
